import {
  FinalResults,
  FinalResultsRound,
  FinalResultsRow,
  RoundResult,
  RoundType,
} from "../interfaces/ICompetitions";

type SortableRound = Pick<RoundResult, "name" | "stageCode">;

const MAX_STAGE_ORDER = Number.MAX_SAFE_INTEGER;

const getNormalizedValue = (value?: string): string => (value || "").trim().toUpperCase();

const getStageOrderFromStageCode = (
  stageCode?: string,
): { stageOrder: number; playoffOrder: number } | null => {
  const normalizedStageCode = getNormalizedValue(stageCode);

  if (!normalizedStageCode) {
    return null;
  }

  if (normalizedStageCode === "F" || normalizedStageCode === "FINAL") {
    return { stageOrder: 1_000_000, playoffOrder: 0 };
  }

  const roundMatch = normalizedStageCode.match(/^R(\d+)(.*)$/);
  if (!roundMatch) {
    return null;
  }

  const denominator = Number(roundMatch[1]);
  if (Number.isNaN(denominator)) {
    return null;
  }

  return {
    stageOrder: 100_000 - denominator,
    playoffOrder: roundMatch[2] ? 1 : 0,
  };
};

const getStageOrderFromName = (
  roundName?: string,
): { stageOrder: number; playoffOrder: number } | null => {
  const normalizedRoundName = getNormalizedValue(roundName);

  if (!normalizedRoundName) {
    return null;
  }

  if (normalizedRoundName === "FINAŁ" || normalizedRoundName === "FINAL") {
    return { stageOrder: 1_000_000, playoffOrder: 0 };
  }

  const roundMatch = normalizedRoundName.match(/^1\/(\d+)(\s*FINAŁU)?(.*)$/);
  if (!roundMatch) {
    return null;
  }

  const denominator = Number(roundMatch[1]);
  if (Number.isNaN(denominator)) {
    return null;
  }

  return {
    stageOrder: 100_000 - denominator,
    playoffOrder: /DOGRYWK/i.test(roundMatch[3] || "") ? 1 : 0,
  };
};

const getRoundSortMeta = (round: SortableRound) => {
  const stageData =
    getStageOrderFromStageCode(round.stageCode) || getStageOrderFromName(round.name);

  return {
    isKnown: Boolean(stageData),
    stageOrder: stageData?.stageOrder ?? MAX_STAGE_ORDER,
    playoffOrder: stageData?.playoffOrder ?? 0,
    name: round.name || "",
  };
};

const compareRoundEntries = (
  roundA: SortableRound,
  roundB: SortableRound,
  direction: "asc" | "desc",
): number => {
  const metaA = getRoundSortMeta(roundA);
  const metaB = getRoundSortMeta(roundB);

  if (metaA.isKnown !== metaB.isKnown) {
    return metaA.isKnown ? -1 : 1;
  }

  if (metaA.isKnown && metaB.isKnown) {
    if (metaA.stageOrder !== metaB.stageOrder) {
      return direction === "asc"
        ? metaA.stageOrder - metaB.stageOrder
        : metaB.stageOrder - metaA.stageOrder;
    }

    if (metaA.playoffOrder !== metaB.playoffOrder) {
      return direction === "asc"
        ? metaA.playoffOrder - metaB.playoffOrder
        : metaB.playoffOrder - metaA.playoffOrder;
    }
  }

  return metaA.name.localeCompare(metaB.name, "pl", { numeric: true, sensitivity: "base" });
};

/**
 * Sortuje rundy według specjalnej kolejności
 * @param rounds - Tablica rund do posortowania
 * @param direction - Kierunek sortowania: "desc" (malejąco - FINAŁ na górze) lub "asc" (rosnąco)
 */
export const sortRounds = <T extends SortableRound>(
  rounds: T[],
  direction: "asc" | "desc" = "desc",
): T[] => [...rounds].sort((roundA, roundB) => compareRoundEntries(roundA, roundB, direction));

/** Ostatnia runda finałowa w zestawie kolumn — wg niej rozpoznajemy „nie wystąpił” (wszyscy sędziowie „_”). */
export const isRowDidNotPerformAllJudges = (
  row: FinalResultsRow,
  rounds: FinalResultsRound[],
): boolean => {
  const finalIndices = rounds
    .map((r, i) => (Number(r.type) === RoundType.FINAL ? i : -1))
    .filter((i) => i >= 0);
  if (finalIndices.length === 0) return false;
  const lastFinalIdx = finalIndices[finalIndices.length - 1];
  const cell = row.rounds[lastFinalIdx]?.trim() ?? "";
  if (!cell) return false;
  const parts = cell.split(/\s+/).filter(Boolean);
  return parts.length > 0 && parts.every((p) => p === "_");
};

/**
 * Sortuje kolumny rund (jak dotąd) oraz przesuwa na koniec wiersze „nie wystąpił” (wszyscy sędziowie „_” w finałowej rundzie).
 */
export const prepareFinalResultsForDisplay = (finalResults: FinalResults): FinalResults => {
  const s = sortFinalResults(finalResults);
  const rows = [...s.rows].sort((a, b) => {
    const aD = isRowDidNotPerformAllJudges(a, s.rounds);
    const bD = isRowDidNotPerformAllJudges(b, s.rounds);
    if (aD !== bD) return aD ? 1 : -1;
    const ap = a.place ?? 0;
    const bp = b.place ?? 0;
    if (ap !== bp) return ap - bp;
    return String(a.presentationId).localeCompare(String(b.presentationId), undefined, { numeric: true });
  });
  return { ...s, rows };
};

export const sortFinalResults = (finalResults: FinalResults): FinalResults => {
  const roundsWithIndexes = finalResults.rounds.map((round, index) => ({
    round,
    index,
  }));

  const sortedRoundsWithIndexes = [...roundsWithIndexes].sort((roundA, roundB) =>
    compareRoundEntries(roundA.round, roundB.round, "asc"),
  );

  const roundsChanged = sortedRoundsWithIndexes.some(({ index }, sortedIndex) => index !== sortedIndex);
  if (!roundsChanged) {
    return finalResults;
  }

  const sortedRounds: FinalResultsRound[] = sortedRoundsWithIndexes.map(({ round }) => round);

  return {
    ...finalResults,
    rounds: sortedRounds,
    rows: finalResults.rows.map((row) => ({
      ...row,
      rounds: sortedRoundsWithIndexes.map(({ round, index }) => {
        const mappedRoundResult = row.roundResults?.find((roundResult) => roundResult.roundId === round.roundId);
        if (mappedRoundResult) {
          return mappedRoundResult.value ?? "";
        }

        return row.rounds[index] ?? "";
      }),
      roundResults: sortedRounds.map((round, sortedIndex) => ({
        roundId: round.roundId,
        value: row.roundResults?.find((roundResult) => roundResult.roundId === round.roundId)?.value ?? row.rounds[sortedRoundsWithIndexes[sortedIndex].index] ?? "",
      })),
    })),
  };
};

